import React, { useState, useEffect } from "react";
import SideBarNew from "../../SideBarNew";
import TopBarNew from "../../TopBarNew";
import MenuIcon from "../../icons/MenuIcon.svg";
import TowerIcon2 from "../../icons/TowerIcon2.svg";
import { getActiveTeam, getFeaturesPermissions, PICSTORK_FEATURES } from "../../Teams";
import AppConstants from "../../AppConstants";
import {
  checkSubscriptionAndStorage,
  addUserAnalytic,
  updateOutTime,
} from "../../ReusableComponents/reusableFunctions";
import TowerImagesAnnotation from "./TowerImagesAnnotation";

const PickStorkTowerDetector = (props) => {
  const [user, setUser] = useState({});
  const [type, setType] = useState("");
  const [showBackIcon] = useState(true);
  const [sidebarMode, setSidebarMode] = useState("shrunk");
  const [outId, setOutId] = useState("");
  const [permissions, setPermissions] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [annotationSet, setAnnotationSet] = useState(null);
  const [classes, setClasses] = useState([]);
  const [task, setTask] = useState(null);
  const [project, setProject] = useState(null);
  const [plan, setPlan] = useState(null);
  const [images, setImages] = useState([]);

  // ---- Lifecycle: componentWillUnmount
  useEffect(() => {
    return () => {
      updateOutTime(outId);
    };
  }, [outId]);

  // ---- Lifecycle: componentWillMount
  useEffect(() => {
    document.title = "PicStork - Tower Detector";

    // Get permissions first
    getFeaturesPermissions([PICSTORK_FEATURES.IMAGECOLLECTIONS, PICSTORK_FEATURES.ANNOTATIONS])
      .then((perms) => {
        setPermissions(perms);
        console.log("Permissions obtained:", perms);
      })
      .catch((error) => {
        console.error("Error getting permissions:", error);
        // Set a default permissions object to prevent blocking
        setPermissions({
          container: "default",
          sasToken: "default"
        });
      });

    getActiveTeam().then((activeTeam) => {
      // ✅ check subscription
      checkSubscriptionAndStorage(AppConstants.SERVICES.PICSTORK).then(
        (data) => {
          if (data.error || data.storageExceed || data.isExpired) {
            console.warn("Subscription issue:", data);
          }
        }
      );

      // ✅ Analytics
      addUserAnalytic(props?.location?.pathname || "/picstork/detector").then(
        (id) => {
          setOutId(id);
        }
      );

      if (props.location?.state) {
        const { 
          user, 
          type, 
          sidebarMode, 
          image, 
          annotationSet, 
          classes, 
          task, 
          project, 
          plan, 
          images 
        } = props.location.state;
        
        setUser(user);
        setSidebarMode(sidebarMode || "shrunk");
        setType(type);
        setSelectedImage(image); 
        setAnnotationSet(annotationSet);
        setClasses(classes || []);
        setTask(task);
        setProject(project);
        setPlan(plan);
        setImages(images || []);
        
        console.log("Navigation data received:", {
          image,
          annotationSet,
          classes,
          task,
          project,
          plan,
          images
        });
      }
    });
  }, [props.location?.pathname, props.location?.state]);

  // ---- Sidebar Config
  const sidebarConfigurations = {
    options: showBackIcon
      ? [
          { icon: MenuIcon, text: "Home", path: "/picstork/dashboard" },
          { icon: TowerIcon2, text: "Projects", path: "/picstork/towers" },
        ]
      : null,
    user,
    type,
    mode: sidebarMode,
    picStorkHelp: true,
  };

  return (
    <div className="wrapper">
      {/* Sidebar */}
      <SideBarNew
        config={sidebarConfigurations}
        showMainDashboardIcons={showBackIcon}
        parentSidebarSwitch={() =>
          setSidebarMode(sidebarMode === "shrunk" ? "expanded" : "shrunk")
        }
      />

      <div
        className="right-content"
        style={{ backgroundColor: "#EAEDF0", minHeight: "100vh" }}
        onClick={() => setSidebarMode("shrunk")}
      >
        <TopBarNew breadcrumb={["Dashboard", "Tower Detector"]} />

        <div
          className="main-content"
          style={{
            padding: "0px",
            height: "calc(100vh - 80px)",
          }}
        >
          

          {permissions && selectedImage ? (
            <TowerImagesAnnotation
              permissions={permissions}
              location={{
                state: {
                  user: user,
                  type: type,
                  image: selectedImage,
                  project: project,
                  plan: plan,
                  classes: classes,
                  task: task,
                  annotationSet: annotationSet,
                  images: images
                }
              }}
              updateTaskDetails={(step, task) => {
                if (task) setTask(task);
                console.log("Task updated:", task);
              }}
              setAnnotationDone={() => {
                console.log("Annotation completed");
                // You can add navigation logic here if needed
              }}
            />
          ) : (
            <div
              style={{
                padding: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                height: "100%",
              }}
            >
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1F1C45",
                  marginBottom: "8px",
                }}
              >
                {selectedImage ? selectedImage.file_name || "Tower Image" : "No Image Selected"}
              </div>

              <div
                style={{
                  flex: 1,
                  position: "relative",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {!permissions ? (
                  <div style={{ textAlign: "center" }}>
                    <p><span role="img" aria-label="Loading">⏳</span> Loading permissions...</p>
                    <p>Please wait while we set up the annotation interface</p>
                  </div>
                ) : !selectedImage ? (
                  <div style={{ textAlign: "center" }}>
                    <p><span role="img" aria-label="Error">❌</span> No image selected for annotation</p>
                    <p>Please navigate from the Tower Images page</p>
                    <button 
                      onClick={() => window.history.back()}
                      style={{
                        padding: "10px 20px",
                        backgroundColor: "#007bff",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                        marginTop: "10px"
                      }}
                    >
                      Go Back to Tower Images
                    </button>
                  </div>
                ) : (
                  <div style={{ textAlign: "center" }}>
                    <p><span role="img" aria-label="Loading">⏳</span> Loading annotation interface...</p>
                    <p>Image: {selectedImage.file_name}</p>
                    <p>Please wait...</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PickStorkTowerDetector;
